import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { apiService } from '@/services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';

export const Route = createFileRoute('/org/$orgSlug/portal/auto-login')({
  component: AutoLogin,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      token: (search.token as string) || '',
      redirect: (search.redirect as string) || undefined,
    };
  },
});

function AutoLogin() {
  const { orgSlug } = Route.useParams();
  const { token, redirect } = Route.useSearch();
  const navigate = useNavigate();
  const { authenticateWithMagicLink, customer, isAuthenticated } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Prevent double-execution in React StrictMode
  const hasAttemptedRef = useRef(false);

  const performAutoLogin = useCallback(async () => {
    try {
      console.log('Auto-login effect running', {
        hasAttempted: hasAttemptedRef.current,
        isAuthenticated,
        hasCustomer: !!customer
      });

      // If already authenticated, just redirect
      if (isAuthenticated && customer) {
        console.log('Already authenticated, redirecting...');
        setStatus('success');

        // Determine redirect path based on active tickets
        let redirectPath = `/org/${orgSlug}/portal/tickets`; // Default to tickets

        // Use custom redirect if provided, otherwise check for active tickets
        if (redirect) {
          redirectPath = redirect;
        } else {
          try {
            const statsResponse = await apiService.getCustomerTicketStats();
            if (statsResponse.success && statsResponse.data) {
              const { openTickets, inProgressTickets, pendingTickets } = statsResponse.data;
              const activeTickets = openTickets + inProgressTickets + pendingTickets;

              if (activeTickets === 0) {
                redirectPath = `/org/${orgSlug}/portal/submit-ticket`;
              }
            }
          } catch {
            // On error, default to tickets page
          }
        }

        navigate({ to: redirectPath });
        return;
      }

      // Prevent double-execution
      if (hasAttemptedRef.current) {
        console.log('Auto-login aborted - already attempted');
        return;
      }

      if (!token) {
        setStatus('error');
        setErrorMessage('Invalid or missing authentication token');
        return;
      }

      // Get organization ID from portal context
      const portalContext = window.__PORTAL_CONTEXT__;
      if (!portalContext?.organizationInfo?.organizationId) {
        setStatus('error');
        setErrorMessage('Organization context not available');
        return;
      }

      const organizationId = portalContext.organizationInfo.organizationId;

      // Mark as attempted BEFORE making the call
      hasAttemptedRef.current = true;
      console.log('Making authentication request...');

      // Attempt to authenticate with the magic link token
      const success = await authenticateWithMagicLink(token, organizationId);

      if (success) {
        console.log('Authentication successful');
        setStatus('success');

        // Determine redirect path based on active tickets
        let redirectPath = `/org/${orgSlug}/portal/tickets`; // Default to tickets

        // Use custom redirect if provided, otherwise check for active tickets
        if (redirect) {
          redirectPath = redirect;
        } else {
          try {
            // Check if customer has active tickets (Open, InProgress, or Pending)
            const statsResponse = await apiService.getCustomerTicketStats();
            if (statsResponse.success && statsResponse.data) {
              const { openTickets, inProgressTickets, pendingTickets } = statsResponse.data;
              const activeTickets = openTickets + inProgressTickets + pendingTickets;

              console.log('Active tickets count:', activeTickets);

              if (activeTickets === 0) {
                // No active tickets, redirect to submit ticket page
                redirectPath = `/org/${orgSlug}/portal/submit-ticket`;
              }
            }
          } catch (statsError) {
            console.error('Failed to fetch ticket stats, defaulting to tickets page:', statsError);
            // On error, default to tickets page
          }
        }

        // Redirect after a brief delay
        setTimeout(() => {
          navigate({ to: redirectPath });
        }, 1500);
      } else {
        console.log('Authentication failed');
        throw new Error('Authentication failed');
      }
    } catch (error: unknown) {
      console.error('Auto-login error:', error);
      setStatus('error');
      const errorMessage = error instanceof Error && 'response' in error &&
        typeof error.response === 'object' && error.response !== null &&
        'data' in error.response &&
        typeof error.response.data === 'object' && error.response.data !== null &&
        'message' in error.response.data &&
        typeof error.response.data.message === 'string'
        ? error.response.data.message
        : error instanceof Error
        ? error.message
        : 'Authentication failed. The link may have expired or already been used.';
      setErrorMessage(errorMessage);

      // Redirect to login page after error
      setTimeout(() => {
        navigate({ to: `/org/${orgSlug}/portal/login` });
      }, 3000);
    }
  }, [token, orgSlug, redirect, isAuthenticated, customer, authenticateWithMagicLink, navigate]);

  useEffect(() => {
    performAutoLogin();
  }, [performAutoLogin]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">
            {status === 'loading' && 'Signing you in...'}
            {status === 'success' && 'Success!'}
            {status === 'error' && 'Authentication Failed'}
          </CardTitle>
          <CardDescription>
            {status === 'loading' && 'Please wait while we authenticate your access'}
            {status === 'success' && 'You have been successfully authenticated'}
            {status === 'error' && 'There was a problem signing you in'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-6">
          {status === 'loading' && (
            <>
              <Loader2 className="h-16 w-16 animate-spin text-blue-500" />
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                Authenticating your credentials...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle2 className="h-16 w-16 text-green-500" />
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                Redirecting you to the customer portal...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="h-16 w-16 text-red-500" />
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                {errorMessage}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500 text-center">
                Redirecting you to the login page...
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
